'use client';

/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */

import PropTypes from 'prop-types';
import {useSnackbar} from "notistack";
import debounce from "lodash.debounce";
import {useForm} from 'react-hook-form';
import {useRouter} from 'next/navigation'
import {yupResolver} from "@hookform/resolvers/yup";
import {useRef, useMemo, useState, useEffect} from 'react';
import {sendRecipe} from "@/services/recipe/save-api";
import {mapFormToRecipeRequestDto} from "@/services/recipe/mapper";

import {
  Step,
  Stepper,
  StepLabel, Container
} from '@mui/material';

import {paths} from 'src/routes/paths';

import {usePersistForm} from "src/hooks/use-persist-form";

import {useTranslate} from 'src/locales';

import FormProvider from "src/components/hook-form";
import {useSettingsContext} from 'src/components/settings';
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";

import getFormData from "./get-form-data";
import BasicDataForm from "./basic-data-form";
import FinalDataForm from "./final-data-form";
import IngredientsForm from "./ingredients-form";
import PreparationFrom from "./preparation-form";
import {getNewRecipeBasicDataSchema, getNewRecipeFinalDataSchema, getNewRecipeIngredientDataSchema, getNewRecipePreparationDataSchema} from "./form-validation-rules";

const FORM_DATA_KEY = 'recipe-new-form';

function RecipeNewEditForm({isEdit, currentRecipe}) {
  const router = useRouter();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslate();
  const settings = useSettingsContext();
  const steps = [t('page.recipe.add.steps.basic_information'), t('page.recipe.add.steps.preparation'), t('page.recipe.add.steps.ingredients'), t('page.recipe.add.steps.summary')];

  const {
    save: savePersistedData,
    load: loadPersistedData,
    clear: clearPersistedData
  } = usePersistForm({localStorageKey: FORM_DATA_KEY});

  let persistedFormData = null;
  if (!isEdit) {
    persistedFormData = loadPersistedData();
  }

  const [activeStep, setActiveStep] = useState(0);

  const stepToDataSchemaMap = {
    0: getNewRecipeBasicDataSchema(t),
    1: getNewRecipePreparationDataSchema(t),
    2: getNewRecipeIngredientDataSchema(t),
    3: getNewRecipeFinalDataSchema(t),
  }

  const persistedRecipe = persistedFormData?.formData;
  const defaultValues = useMemo(
    () => {
      if (!persistedRecipe) {
        return getFormData(currentRecipe);
      }

      return persistedRecipe;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persistedRecipe, currentRecipe]
  );

  const methods = useForm({
    resolver: yupResolver(stepToDataSchemaMap[activeStep]),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    getValues,
    formState: {errors}
  } = methods;

  const saveData = () => {
    console.log(`Saved form data to localstorage at ${Date.now()}`);
    savePersistedData(getValues(), 1000 * 60 * 60 * 24 * 7);
  };
  const {current: throttledSaveData} = useRef(debounce(saveData, 3000));

  useEffect(() => {
    const rhfWatch = watch(() => {
      if (!isEdit && throttledSaveData.current !== null) {
        throttledSaveData();
      }
    });

    return () => rhfWatch.unsubscribe()
  }, [watch, isEdit, throttledSaveData]);

  useEffect(() => {
    let firstErrorField = Object.keys(errors).reduce((field, a) => errors[field] ? field : a, null);
    let firstError = errors[firstErrorField];

    if (!firstError) {
      return;
    }

    while (Array.isArray(firstError) || (typeof firstError === 'object' && !firstError.message)) {
      const firstKey = Object.keys(firstError)[0];

      if (Array.isArray(firstError)) {
        firstErrorField += `[${firstKey}]`;
      } else {
        firstErrorField += `.${firstKey}`;
      }

      firstError = firstError[firstKey];
    }

    document.querySelector(`[name="${firstErrorField}"]`)?.scrollIntoView(false, {
      behavior: "smooth",
      block: "center",
      inline: "nearest"
    });
    enqueueSnackbar(firstError?.message, {variant: `error`});
  }, [enqueueSnackbar, errors])

  const onSubmit = async (data) => {
    if (activeStep >= 3) {
      try {
        if (isEdit) {
          data.id = currentRecipe.pimcoreRecipeId;
        }

        const recipeRequestDto = mapFormToRecipeRequestDto(data);
        const {previewUrl} = await sendRecipe(recipeRequestDto);

        reset(defaultValues);
        throttledSaveData.cancel();
        throttledSaveData.current = null;
        clearPersistedData();
        enqueueSnackbar(!isEdit ? 'Added new recipe!' : 'Updated recipe!');
        router.push(previewUrl);
      } catch (ex) {
        if (!!ex && !!ex.message) {
          enqueueSnackbar(!isEdit ? `Error. Could not add new recipe. Details: ${ex.message}` : `Error. Could not update recipe. Details: ${ex.message}`, {variant: `error`});
        } else {
          enqueueSnackbar(!isEdit ? 'Unknown error. Could not add new recipe.' : 'Error. Could not update recipe.', {variant: `error`});
        }
      }
    } else {
      onSubmitIncrementStep();
    }
  };


  const onSubmitIncrementStep = () => {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0;
    saveData();
    setActiveStep(activeStep + 1);
  };

  const handleStep = (step) => () => {
    if (step < activeStep) {
      setActiveStep(step);
    }
  };

  const renderForStep = (content, step) => activeStep === step
    ? content
    : '';

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading={isEdit ? 'Edycja przepisu' : 'Dodaj nowy przepis'}
        links={[
          {name: 'Strona główna', href: paths.homepage},
          {
            name: 'Przepisy',
            href: paths.recipe.browse,
          },
          {name: isEdit ? 'Edycja przepisu' : 'Dodaj nowy przepis'},
        ]}
      />
      <Stepper activeStep={activeStep} sx={{
        mb: 4,
        mt: 4,
        flexDirection: {xs: 'column', md: 'row'},
      }}>
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};
          return (
            <Step key={label} {...stepProps} onClick={handleStep(index)} sx={{
              width: {xs: '100%', md: 'initial'},
              border: {xs: '2px solid #eee', md: 'none'},
              borderRadius: {xs: 1, md: 0},
              p: {xs: 1, md: 'initial'},
              mb: {xs: 1, md: 0}
            }}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {renderForStep(<BasicDataForm id="basic-data" isEdit={isEdit} currentRecipe={currentRecipe}/>, 0)}
        {renderForStep(<PreparationFrom id="preparation-data" isEdit={isEdit} currentRecipe={currentRecipe}/>, 1)}
        {renderForStep(<IngredientsForm id="ingredients-data" isEdit={isEdit} currentRecipe={currentRecipe}/>, 2)}
        {renderForStep(<FinalDataForm id="final-data" isEdit={isEdit} currentRecipe={currentRecipe}/>, 3)}
      </FormProvider>
    </Container>
  );
}

RecipeNewEditForm.propTypes = {
  isEdit: PropTypes.bool,
  currentRecipe: PropTypes.object
};

export default RecipeNewEditForm;
